import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { brand } from '../brand';
import BrandLogo from './BrandLogo';

/**
 * Standalone login screen used on white-label domains (no marketing landing page).
 */
const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(email, password);
            if (!result.success) {
                // Show specific error message, especially for rate limiting
                const errorMsg = result.error || 'Failed to log in. Please check your email and password.';
                setError(errorMsg);
                
                // If rate limited, suggest waiting
                if (errorMsg.toLowerCase().includes('too many requests') || errorMsg.toLowerCase().includes('rate limit')) {
                    setError('Too many login attempts. Please wait a moment and try again.');
                }
            }
        } catch (err) {
            const errorMsg = err.message || 'Failed to log in. Please check your email and password.';
            if (errorMsg.toLowerCase().includes('too many requests') || errorMsg.toLowerCase().includes('rate limit')) {
                setError('Too many login attempts. Please wait a moment and try again.');
            } else {
                setError('Failed to log in. Please check your email and password.');
            }
            console.error("Login Error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg">
                <div className="flex flex-col items-center">
                    <BrandLogo size="lg" />
                    <h2 className="mt-4 text-center text-2xl font-bold text-gray-900">{brand.name}</h2>
                    <p className="mt-1 text-center text-sm text-gray-500">Sign in to your account</p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleLogin}>
                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:z-10 sm:text-sm disabled:bg-gray-100"
                                placeholder="Email address"
                            />
                        </div>
                        <div>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:z-10 sm:text-sm disabled:bg-gray-100"
                                placeholder="Password"
                            />
                        </div>
                    </div>
                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{ backgroundColor: brand.primaryColor }}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white hover:opacity-90 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </div>
                </form>
                {brand.homeUrl && (
                    <p className="text-center text-sm">
                        <a href={brand.homeUrl} className="text-gray-500 hover:text-gray-700">
                            &larr; Back to {brand.homeUrl.replace(/^https?:\/\//, '')}
                        </a>
                    </p>
                )}
            </div>
        </div>
    );
};

export default LoginPage;
